import type { ContentVersion } from '@opallabs/api'
import {
  Avatar,
  Button,
  Checkbox,
  Icon,
  Pill,
  ToggleButtonGroup,
} from '@opallabs/lapidary'
import { filterVersions, isMilestone } from './isMilestone'
import * as styles from './styles'
import type { VersionFilter } from './types'

const formatTimestamp = (isoDate: string): string =>
  new Date(isoDate).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

type VersionsRailProps = {
  versions: ContentVersion[]
  activeId: string | null
  filter: VersionFilter
  compareMode: boolean
  comparedIds: string[]
  /** Deleting a version needs edit permission on the content; viewers only read them. */
  canEdit: boolean
  onFilterChange: (filter: VersionFilter) => void
  onSelect: (id: string) => void
  onToggleCompareMode: () => void
  onToggleCompared: (id: string) => void
  onHide: (id: string) => void
}

export const VersionsRail: React.VFC<VersionsRailProps> = ({
  versions,
  activeId,
  filter,
  compareMode,
  comparedIds,
  canEdit,
  onFilterChange,
  onSelect,
  onToggleCompareMode,
  onToggleCompared,
  onHide,
}) => {
  // The newest version overall is the live content, regardless of the active filter.
  const currentId = versions[0]?.id
  const visibleVersions = filterVersions(versions, filter)

  return (
    <>
      <div css={styles.railFilter}>
        <ToggleButtonGroup.Root
          initialValue={filter}
          offVariant="tertiary"
          onVariant="secondary"
          onValueChange={(value) => onFilterChange(value as VersionFilter)}
        >
          <ToggleButtonGroup.Button
            value="all"
            buttonText="All"
            name="Show all versions"
          />
          <ToggleButtonGroup.Button
            value="milestones"
            buttonText="Milestones"
            name="Show milestones only"
            // Nothing is labeled, so the filter has nothing to show. All stays
            // available, so switching to it is never blocked.
            disabled={!versions.some(isMilestone)}
          />
        </ToggleButtonGroup.Root>
        <Button
          buttonText="Compare"
          variant={compareMode ? 'secondary' : 'tertiary'}
          name={compareMode ? 'Exit compare mode' : 'Compare versions'}
          // Comparing takes a pair. Never disabled while comparing, or there
          // would be no way back out.
          disabled={!compareMode && visibleVersions.length < 2}
          onClick={onToggleCompareMode}
        />
      </div>
      {visibleVersions.length === 0 ? (
        // With versions to show, an empty list can only be the filter.
        <p css={styles.railEmpty}>
          {versions.length === 0 ? 'No versions yet.' : 'No milestones yet.'}
        </p>
      ) : (
        <ul css={styles.railList} aria-label="Versions">
          {visibleVersions.map((version) => {
            const isChecked = comparedIds.includes(version.id)
            return (
              <VersionRow
                key={version.id}
                version={version}
                isSelected={compareMode ? isChecked : version.id === activeId}
                isCurrent={version.id === currentId}
                compareMode={compareMode}
                isChecked={isChecked}
                canDelete={canEdit}
                onActivate={() =>
                  compareMode
                    ? onToggleCompared(version.id)
                    : onSelect(version.id)
                }
                onHide={() => onHide(version.id)}
              />
            )
          })}
        </ul>
      )}
    </>
  )
}

type VersionRowProps = {
  version: ContentVersion
  isSelected: boolean
  isCurrent: boolean
  compareMode: boolean
  isChecked: boolean
  canDelete: boolean
  onActivate: () => void
  onHide: () => void
}

const VersionRow: React.VFC<VersionRowProps> = ({
  version,
  isSelected,
  isCurrent,
  compareMode,
  isChecked,
  canDelete,
  onActivate,
  onHide,
}) => {
  const authorId = `version-author-${version.id}`
  const timeId = `version-time-${version.id}`

  return (
    <li css={styles.rowItem(isSelected)}>
      {/* The row's trigger is a real button stretched over the row, and a
          sibling of the row's other controls rather than their parent: a
          button cannot contain a checkbox or another button, and claiming
          otherwise told assistive technology the wrong thing about all three.
          Named by the author and date, which is what tells the rows apart. */}
      <button
        type="button"
        css={styles.rowTrigger}
        aria-labelledby={`${authorId} ${timeId}`}
        // Single-select, so the row is the current one rather than pressed. In
        // compare mode the checkbox carries the state instead.
        aria-current={!compareMode && isSelected ? true : undefined}
        onClick={onActivate}
      />
      {compareMode && (
        // The trigger above owns the interaction; the checkbox reflects its
        // state, and sits under the trigger rather than competing with it.
        <Checkbox
          checked={isChecked}
          readOnly
          tabIndex={-1}
          labedlledbyId={authorId}
          css={styles.rowCheckbox}
        />
      )}
      <Avatar fullName={version.authorName} size={20} />
      <div css={styles.rowDetails}>
        <div css={styles.rowHeader}>
          <div css={styles.rowNameGroup}>
            <span id={authorId} css={styles.rowName}>
              {version.authorName}
            </span>
            {/* Labels are set from the preview byline; the rail only shows them. */}
            {version.label && (
              <Pill text={version.label} color="purple" size="xsmall" solid />
            )}
            {isCurrent && (
              <Pill text="Current" color="green" size="xsmall" solid />
            )}
          </div>
          {/* The newest row can't be deleted, so the right slot is trash-only. */}
          {!isCurrent && canDelete && (
            <button
              type="button"
              css={styles.deleteButton}
              aria-label="Delete version"
              onClick={onHide}
            >
              <Icon ariaHidden glyph="gem-trash" />
            </button>
          )}
        </div>
        <div css={styles.rowMeta}>
          <span id={timeId}>{formatTimestamp(version.createdAt)}</span>
          {/* Resolved feedback is done with, so the badge counts what's open. */}
          {version.unresolvedAnnotationsCount > 0 && (
            <>
              <span aria-hidden>·</span>
              <span
                css={styles.rowAnnotationCount}
                aria-label={`${version.unresolvedAnnotationsCount} open feedback`}
              >
                <Icon glyph="gem-comment" size={12} ariaHidden />
                {version.unresolvedAnnotationsCount}
              </span>
            </>
          )}
        </div>
      </div>
    </li>
  )
}
